import React from "react";

import { ReactComponent as BustleTick } from "@images/bustle_tick_icon.svg";
import classNames from "classnames";
import { stat } from "fs";
import { keyBy } from "lodash-es";
import { useTranslation } from "react-i18next";

import { Price } from "../../Atoms";
import { bustleBeeForCustomer, BustleBee } from "../Customer/bustleBees";
import { useLoadedCustomer } from "../Customer/queries";
import { Customer } from "../Customer/schema";
import { OrderItem } from "../CustomerTab/schema";
import { BillSplit, OrderItemClaim, OrderItemStatus } from "./schema";

export const OrderItemSwitch = ({
  orderItem,
  status,
  disabled,
  billSplit,
  deleteOrderItemClaim,
  unclaimable,
  claim,
  claimItem,
}: {
  orderItem: OrderItem;
  claim?: OrderItemClaim & { customer: Customer };
  status: OrderItemStatus;
  claimItem: () => void;
  billSplit: BillSplit;
  deleteOrderItemClaim: () => void;
  disabled?: boolean;
  unclaimable?: boolean;
}) => {
  const { t } = useTranslation("onsite");
  const bustleBee = claim && bustleBeeForCustomer(claim.customer);
  switch (status) {
    case "yours":
      return (
        <ClaimBox
          status={status}
          ourClaim
          disabled={disabled}
          onClick={deleteOrderItemClaim}
        >
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              <div className="h-32 w-32 text-venue-tertiary">
                <BustleTick />
              </div>
            }
          />
        </ClaimBox>
      );

    case "others_claimed":
      return claim ? (
        <ClaimBox status={status} theirClaim disabled>
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              bustleBee && (
                <div className="flex w-full items-center justify-end gap-8">
                  <div className="flex-1">Claimed </div>
                  <BustleBeeIcon bustleBee={bustleBee} />
                </div>
              )
            }
          />
        </ClaimBox>
      ) : null;
    case "others_paid":
      return claim ? (
        <ClaimBox status={status} theirClaim disabled>
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              bustleBee && (
                <div className="flex w-full items-center justify-end gap-8">
                  <div className="flex-1">Paid </div>
                  <BustleBeeIcon bustleBee={bustleBee} />
                </div>
              )
            }
          />
        </ClaimBox>
      ) : null;
    case "paid_at_till":
      return (
        <ClaimBox status={status} theirClaim disabled>
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              <div className=""> {t("billSplit.paidAtTill")}</div>
            }
          />
        </ClaimBox>
      );
    case "unclaimed":
      return (
        <div className="space-y-4">
          <ClaimBox
            status={unclaimable ? "unclaimable" : status}
            onClick={claimItem}
            disabled={disabled || unclaimable}
            warningMessage={
              unclaimable ? t("billSplit.unclaimable") : undefined
            }
          >
            <ClaimableOrderItemContent orderItem={orderItem} />
          </ClaimBox>
        </div>
      );
  }
};
export const ClaimableOrderItemContent = ({
  orderItem,
  claimedContent,
  warningMessage,
}: {
  orderItem: OrderItem;
  claimedContent?: React.ReactNode;
  warningMessage?: React.ReactNode;
}) => {
  const { t } = useTranslation("onsite");
  return (
    <>
      <div className="flex w-full items-center justify-between gap-12">
        {orderItem.itemHeroImageThumbnailUrl && (
          <div className="flex-none">
            <div className="aspect-h-1 aspect-w-1 w-[80px] flex-none">
              <img
                src={orderItem.itemHeroImageThumbnailUrl}
                className="rounded-venue-brand object-cover"
              />
            </div>
          </div>
        )}
        <div className="flex-1">
          <div className="font-bold">
            {t("billSplit.itemNameWithMultiplier", {
              itemName: orderItem.itemName,
              count: orderItem.multiplier,
            })}
          </div>
          <div>
            <Price value={orderItem.totalSalesPriceTaxInclusive} />
          </div>
          <ul className="wrap list-disc text-xs font-normal">
            {orderItem.orderItemMods.map((mod) => mod.name).join(", ")}
          </ul>
        </div>
        <div className="w-1/8 flex justify-end justify-self-end text-right">
          {claimedContent || <div className="h-32 w-32" />}
        </div>
      </div>
      {warningMessage && (
        <div className="text-sm absolute inset-0 flex items-end justify-center text-center font-normal">
          <div className="absolute inset-0 bg-venue-tertiary opacity-80" />
          <div className="relative bg-venue-tertiary p-8">
            {warningMessage && warningMessage}{" "}
          </div>
        </div>
      )}
    </>
  );
};

export const ClaimBox = ({
  children,
  onClick,
  status,
  disabled,
  ourClaim,
  theirClaim,
  warningMessage,
}: {
  children: React.ReactNode;
  status?: OrderItemStatus;
  disabled?: boolean;
  onClick?: () => void;
  ourClaim?: boolean;
  theirClaim?: boolean;
  warningMessage?: React.ReactNode;
}) => {
  const grayedOut =
    !warningMessage &&
    (status == "others_claimed" ||
      status == "others_paid" ||
      status == "paid_at_till" ||
      status == "unclaimable");
  return (
    <div className="relative">
      <div
        onClick={!disabled && !theirClaim && onClick ? onClick : undefined}
        className={classNames(
          "relative flex flex-wrap items-center justify-between gap-8 rounded-venue-brand border-2 p-12 text-base font-medium shadow-venue-brand-style",
          {
            "border-venue-tertiary bg-venue-tertiary": !status,
            "cursor-pointer border-venue-primary bg-venue-primary text-venue-tertiary hover:border-venue-primary":
              status == "yours",
            "cursor-pointer border-venue-tertiary bg-venue-tertiary":
              !disabled && status == "unclaimed",
            "claimed border-venue-tertiary bg-venue-tertiary grayscale":
              status == "others_claimed",
            "paid border-venue-tertiary bg-venue-tertiary grayscale":
              status == "paid_at_till" || status == "others_paid",
            "unclaimable border-venue-tertiary bg-venue-tertiary grayscale":
              status == "unclaimable",
          },
        )}
      >
        {children}
      </div>
      <div
        className={classNames({
          "text-sm absolute inset-0 flex items-end justify-center rounded-venue-brand bg-neutral-300 text-center font-normal opacity-30":
            grayedOut,
        })}
      />
      {warningMessage && (
        <div className="absolute inset-0 flex items-end justify-center text-center text-xs font-normal">
          <div className="absolute inset-0 rounded-venue-brand bg-neutral-300 opacity-30" />
          <div className="relative flex-1 rounded-b-venue-brand bg-venue-tertiary p-8">
            {warningMessage && warningMessage}{" "}
          </div>
        </div>
      )}
    </div>
  );
};

export const RemainderClaimBox = ({
  children,
  onClick,
  status,
  disabled,
  theirClaim,
}: {
  children: React.ReactNode;
  status?: Extract<OrderItemStatus, "yours" | "others_claimed" | "unclaimed">;
  disabled?: boolean;
  onClick?: () => void;
  theirClaim?: boolean;
}) => {
  return (
    <div className="relative">
      <div
        onClick={
          !disabled && status !== "others_claimed" && onClick
            ? onClick
            : undefined
        }
        className={classNames(
          "relative flex flex-wrap items-center justify-between gap-8 rounded-venue-brand border-2 p-12 text-base font-medium shadow-venue-brand-style",
          {
            "border-venue-tertiary bg-venue-tertiary": !status,
            "cursor-pointer border-venue-primary bg-venue-primary text-venue-tertiary hover:border-venue-primary":
              status == "yours",
            "cursor-pointer border-venue-primary bg-venue-tertiary":
              !disabled && status == "unclaimed",
            "claimed border-venue-tertiary bg-venue-tertiary grayscale":
              status == "others_claimed",
          },
        )}
      >
        {children}
      </div>
    </div>
  );
};

export const RemainderClaim = ({
  billSplit,
  customer,
  onClaimRemainder,
  unclaimedTotal,
}: {
  billSplit: BillSplit;
  customer: Customer;
  onClaimRemainder: (customer: Customer | null) => void;
  unclaimedTotal: number;
}) => {
  const { t } = useTranslation("onsite");
  const customers = keyBy(billSplit.customers, "uuid");
  const bustleBee =
    billSplit.remainderClaimerId &&
    bustleBeeForCustomer(customers[billSplit.remainderClaimerId]);

  const claimingCustomer =
    billSplit.remainderClaimerId && customers[billSplit.remainderClaimerId];
  const owner = customers[billSplit.ownerId];
  const ownerBuslteBee = bustleBeeForCustomer(owner);
  const ourClaim = billSplit.remainderClaimerId === customer.uuid;
  const theirClaim = !!billSplit.remainderClaimerId && !ourClaim;
  const hasBeenClaimed = !!billSplit.remainderClaimerId;
  const status: OrderItemStatus = hasBeenClaimed
    ? ourClaim
      ? "yours"
      : "others_claimed"
    : "unclaimed";

  const isOwner = billSplit.ownerId === customer.uuid;
  return (
    <RemainderClaimBox
      status={status}
      theirClaim={theirClaim}
      onClick={() => {
        onClaimRemainder(!billSplit.remainderClaimerId ? customer : null);
      }}
    >
      <div className="flex w-full items-center justify-between gap-12">
        <div>
          <h3 className="text-xl font-bold">{t("billSplit.totalRemaining")}</h3>
          <Price value={unclaimedTotal} />
          <div className="flex-1">
            <div>
              {isOwner
                ? t("billSplit.totalRemaining_ownerDescription")
                : t("billSplit.totalRemaining_description")}
            </div>
          </div>
        </div>
        <div className="w-1/8 flex justify-end justify-self-end text-right">
          {ourClaim && (
            <div className="h-48 w-48 p-4 text-venue-tertiary">
              <BustleTick />
            </div>
          )}
          {!ourClaim && hasBeenClaimed && bustleBee && (
            <BustleBeeIcon bustleBee={bustleBee} />
          )}
        </div>
      </div>
      <div className="mx-auto text-center text-lg font-bold">
        {!hasBeenClaimed && t("billSplit.claimRemainder")}
        {claimingCustomer &&
          theirClaim &&
          t("billSplit.remainderClaimedBy", {
            name: claimingCustomer.nickname,
          })}
        {hasBeenClaimed && ourClaim && t("billSplit.youHaveClaimedRemainder")}
      </div>
    </RemainderClaimBox>
  );
};

const BustleBeeIcon = ({ bustleBee }: { bustleBee: BustleBee }) => {
  return (
    <div className="h-48 w-48 rounded-full bg-white p-4">
      <img src={bustleBee.img_96} alt={bustleBee.alt} />
    </div>
  );
};

export const UnclaimedOrderItem = ({
  orderItem,
  claimItem,
  disabled,
}: {
  orderItem: OrderItem;
  claimItem: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation("onsite");
  return (
    <ClaimBox onClick={claimItem} disabled={disabled}>
      <ClaimableOrderItemContent orderItem={orderItem} />
    </ClaimBox>
  );
};
